import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Output,
  ViewContainerRef,
} from '@angular/core';

import type { Observable } from 'rxjs';
import { first, Subject, switchMap, takeUntil } from 'rxjs';

import { collapse, filterBoolean } from '@recruitee/common';
import { InfoboxComponent } from '@recruitee/components/infobox';
import { I18nService, TranslatePipe } from '@recruitee/i18n';
import {
  BadgeComponent,
  BoxComponent,
  ButtonComponent,
  DialogService,
  DividerComponent,
  HeadingComponent,
  IconButtonComponent,
  IconComponent,
  PlaceloaderComponent,
  SpinnerComponent,
} from '@recruitee/user-interface';

import { HttpErrorHandlerService } from '../../common';
import { ErrorMessagesWrapperComponent } from '../../misc/error-messages-wrapper/error-messages-wrapper.component';
import { FormConfigStoreService } from '../data/src/form-config/form-config.service';
import { FieldMappingFormFieldComponent } from '../field-mapping-form-field/field-mapping-form-field.component';
import type { FormConfig, FormInput, Source } from '../types';
import { FormConfigMode } from '../types';

@Component({
  selector: 'rti-field-mapping-form',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './field-mapping-form.component.html',
  styleUrls: ['./field-mapping-form.component.less'],
  animations: [collapse],
  imports: [
    BadgeComponent,
    BoxComponent,
    ButtonComponent,
    CommonModule,
    DividerComponent,
    ErrorMessagesWrapperComponent,
    FieldMappingFormFieldComponent,
    HeadingComponent,
    IconButtonComponent,
    IconComponent,
    PlaceloaderComponent,
    SpinnerComponent,
    InfoboxComponent,
    TranslatePipe,
  ],
})
export class FieldMappingFormComponent {
  @Input() public formConfig: FormConfig;

  @Input() public integrationName: string;
  @Input() public integrationKind: string;

  @Input() public sourcesLoading: boolean = true;
  @Input() public sources: Source[] = [];

  @Input() public isEditing: boolean = false;

  @Output() public formConfigDeleted: EventEmitter<number> = new EventEmitter();

  @Output()
  public formConfigExpanded: EventEmitter<number> = new EventEmitter();

  @Output()
  public formConfigClosed: EventEmitter<undefined> = new EventEmitter();

  public isLoading: boolean = false;

  private ngOnDestroy$ = new Subject<void>();

  constructor(
    private i18n: I18nService,
    private cdRef: ChangeDetectorRef,
    private formConfigStore: FormConfigStoreService,
    private dialog: DialogService,
    private vcRef: ViewContainerRef,
    private httpErrorHandlerService: HttpErrorHandlerService,
  ) {}

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  public isDynamic(): boolean {
    return this.formConfig.mode === FormConfigMode.DYNAMIC;
  }

  public trackByChecksum(_index: number, { checksum }: FormInput): string {
    return checksum;
  }

  public startEditing(): void {
    this.formConfigExpanded.emit(this.formConfig.id);
  }

  public stopEditing(): void {
    this.formConfigClosed.emit();
  }

  public delete(): void {
    this.confirm$()
      .pipe(
        first(),
        filterBoolean,
        switchMap(() =>
          this.formConfigStore.deleteFormConfig(this.integrationKind, this.formConfig.id),
        ),
        takeUntil(this.ngOnDestroy$.asObservable()),
      )
      .subscribe({
        next: () => {
          this.formConfigDeleted.emit(this.formConfig.id);
        },
        error: error => {
          this.httpErrorHandlerService.default(error);
        },
      });
  }

  public handleFormFieldChange(formInput: FormInput): void {
    this.formConfig.inputs = this.formConfig.inputs?.map(input =>
      input.checksum === formInput.checksum ? formInput : input,
    );

    this.cdRef.detectChanges();
  }

  public badgeLabel(): string {
    return `${this.mappedInputsCount()}/${this.formConfig.inputsCount}`;
  }

  private mappedInputsCount(): number {
    return (this.formConfig.inputs || <FormInput[]>[])
      .flatMap(this.inputs.bind(this))
      .filter((i: FormInput) => i.fieldMappings.length).length;
  }

  private inputs(input: FormInput): FormInput[] {
    return [input, ...(<FormInput[]>(input.inputs || []).flatMap(this.inputs.bind(this)))];
  }

  private confirm$(): Observable<boolean> {
    const title = this.i18n.translate('rti.field_mapping.settings.form_config.delete.dialog.title');
    const message = this.i18n.translate(
      'rti.field_mapping.settings.form_config.delete.dialog.message',
    );

    return this.dialog
      .error(
        {
          title,
          message,
        },
        this.vcRef,
      )
      .pipe(takeUntil(this.ngOnDestroy$.asObservable()));
  }
}
